/**
 * File Upload Utilities
 *
 * Shared utilities for uploading files to presigned URLs.
 * Works on both web and native platforms.
 */

import { Platform } from "react-native";
import { File as ExpoFile } from "expo-file-system";
import { throwApiError } from "@src/packages/api/api-error";
export {
  inferMimeTypeFromFileName,
  resolveUploadContentType,
} from "./fileUploadMime";

export type SelectedFile = {
  uri: string;
  name: string;
  mimeType?: string;
};

/**
 * Reads a file and returns it as an ArrayBuffer or Blob suitable for upload.
 * Handles platform differences between web and native.
 */
export async function readFileForUpload(
  file: SelectedFile,
): Promise<Blob | ArrayBuffer> {
  if (Platform.OS === "web") {
    // Web: Fetch the file as blob
    const response = await fetch(file.uri);
    return response.blob();
  } else {
    // Native: Read file using expo-file-system new API
    const expoFile = new ExpoFile(file.uri);
    const base64Content = await expoFile.base64();

    // Convert base64 to ArrayBuffer
    const binaryString = atob(base64Content);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }
}

/**
 * Uploads a file to a presigned URL.
 */
export async function uploadToPresignedUrl(
  uploadUrl: string,
  fileData: Blob | ArrayBuffer,
  contentType: string,
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    body: fileData,
    headers: {
      "Content-Type": contentType,
    },
  });

  if (!response.ok) {
    throwApiError(
      undefined,
      `Upload failed: ${response.statusText}`,
      "uploads",
      response.status,
    );
  }
}
